import apiClient from '../../utils/apiClient';

export interface ExportPreferences {
  preacher: string;
  preacher_sp: string;
  preacher_managed?: boolean;
  preacher_person_id?: number | null;
  preacher_revision?: number;
  track: '' | '1' | '2';
  options: { ot?: number; psalm?: number; nt?: number; gospel?: number };
  collect: number;
  /** Palm Sunday: '' = the day's default (St Luke's modern), or 'traditional' */
  version?: string;
  /** '' / 'keep' = keep optional verses in [brackets]; 'drop' = remove them */
  optionalVerses?: string;
  /** Easter Vigil: lesson indices "0,3,7" ('' = default) and whether psalms/canticles print */
  vigilLessons?: string;
  vigilResponses?: string;
}
export interface BundleService {
  service_id: number; date: string; time: string; church_id: number;
}

/** The three weekly bulletins, by the suffix of their JSON file. */
export type BulletinKey = '8am' | '10am' | 'sp';
export const ALL_BULLETINS: BulletinKey[] = ['8am', '10am', 'sp'];

/** The coming Sunday: the first date (today or later) with a St Luke's 10 am service. */
export function findComingSunday(services: BundleService[]): string | null {
  const dates = services.filter(s => s.church_id === 2 && s.time.slice(0, 5) === '10:00')
    .map(s => s.date).sort();
  return dates[0] || null;
}

/** Upcoming St Luke's (church 2) and San Lucas (church 3) services, both lists merged. */
export async function fetchBundleServices(limit = 12): Promise<BundleService[]> {
  const [stLukes, sanLucas] = await Promise.all([2, 3].map(churchId =>
    apiClient.get<BundleService[]>('/bulletins/upcoming-services', { params: { limit, churchId } })));
  return [...stLukes.data.map(s => ({ ...s, church_id: 2 })), ...sanLucas.data.map(s => ({ ...s, church_id: 3 }))];
}

/**
 * Export the weekly bulletins for one date from the saved seasonal plan and the
 * Sunday's saved preacher and readings. All three by default; pass `only` for one or two.
 */
export async function createIndesignExports(date: string, services: BundleService[], only: BulletinKey[] = ALL_BULLETINS) {
  const matching = (church: number, time: string) => services.filter(s =>
    s.date === date && s.church_id === church && s.time.slice(0, 5) === time);
  const english = matching(2, '10:00');
  const spanish = matching(3, '12:30');
  const all = ALL_BULLETINS.every(key => only.includes(key));
  if (english.length !== 1 || (only.includes('sp') && spanish.length !== 1)) {
    throw new Error(all
      ? 'This date needs one 10 am service and one 12:30 Spanish service. Use individual exports for a special schedule.'
      : only.includes('sp') && spanish.length !== 1
        ? 'This date needs one 12:30 Spanish service. Use the InDesign page for a special schedule.'
        : 'This date needs one 10 am service. Use the InDesign page for a special schedule.');
  }
  const id = english[0].service_id;
  const [settings, preferences] = await Promise.all([
    apiClient.get(`/bulletins/indesign-settings/services/${id}`),
    apiClient.get<ExportPreferences>(`/bulletins/indesign-settings/services/${id}/export-preferences`),
  ]);
  if (!settings.data.supported) throw new Error('Use the separate Morning Prayer workflow for this Sunday.');
  if (!settings.data.template && !settings.data.override) {
    throw new Error(`Save a seasonal template or Sunday prayer choices before exporting${all ? ' all three' : ''}.`);
  }
  const prefs = preferences.data;
  const shared = new URLSearchParams();
  if (prefs.track) shared.set('track', prefs.track);
  if (prefs.version) shared.set('readingsVersion', prefs.version);
  if (prefs.optionalVerses === 'drop') shared.set('optionalVerses', 'drop');
  if (prefs.vigilLessons) shared.set('vigilLessons', prefs.vigilLessons);
  if (prefs.vigilResponses === 'on') shared.set('vigilResponses', 'on');
  shared.set('collect', String(prefs.collect));
  Object.entries(prefs.options).forEach(([slot, value]) => shared.set(`${slot}Option`, String(value)));
  const variants = [
    { suffix: '8am', id, params: { variant: '8am' } },
    // The 10 am is laid out fully each week too (update-bulletin-10am.jsx).
    { suffix: '10am', id, params: { variant: '10am' } },
    // The 12:30 is rebuilt fully each week (update-bulletin-spanish.jsx) from the
    // 12:30 column of the seasonal plan.
    ...(only.includes('sp') ? [{ suffix: 'sp', id: spanish[0].service_id, params: { lang: 'sp', variant: '1230' } }] : []),
  ].filter(variant => only.includes(variant.suffix as BulletinKey));
  const files = await Promise.all(variants.map(async variant => {
    const params = new URLSearchParams(shared);
    Object.entries(variant.params).forEach(([key, value]) => { if (value) params.set(key, value); });
    const response = await apiClient.get(`/bulletins/services/${variant.id}/export/indesign?${params}`);
    const payload = response.data.preacher_assignment_revision !== undefined
      ? response.data
      : { ...response.data, preacher: prefs.preacher, preacher_sp: prefs.preacher_sp };
    return { filename: `${date.replace(/-/g, '')}_${variant.suffix}.json`, payload };
  }));
  // If someone changes the schedule during the three requests, export again
  // rather than downloading bulletins with different preachers.
  const first = files[0].payload;
  if (files.some(file => file.payload.preacher !== first.preacher || file.payload.preacher_sp !== first.preacher_sp ||
    file.payload.preacher_assignment_revision !== first.preacher_assignment_revision)) {
    throw new Error(`The preaching schedule changed during export. Export${all ? ' all three' : ''} again.`);
  }
  // Keep the 8 am export in the saved history, as the single 8 am export does.
  const eightAm = files.find(file => file.filename.endsWith('_8am.json'));
  if (eightAm) await apiClient.post(`/bulletins/indesign-settings/services/${id}/exports`, eightAm);
  return files.map(file => ({
    blob: new Blob([JSON.stringify(file.payload, null, 2)], { type: 'application/json' }),
    filename: file.filename,
  }));
}

export function downloadExport(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url; link.download = filename;
  document.body.appendChild(link); link.click(); link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
